import { useRef, type CSSProperties, type ReactElement } from 'react';
import { Group, Table, UnstyledButton } from '@mantine/core';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import type { MegaGridColumn, MegaGridDensity, MegaGridSort } from './MegaGridTypes';

interface MegaGridHeaderProps<T> {
    columns: MegaGridColumn<T>[];
    sorts: MegaGridSort[];
    widths: Record<string, number>;
    pinned: Set<string>;
    pinnedOffsets: Record<string, number>;
    density: MegaGridDensity;
    allowResize: boolean;
    onToggleSort: (key: string, additive: boolean) => void;
    onResize: (key: string, width: number) => void;
}

function pinnedStyle(pinned: boolean, offset: number, header: boolean): CSSProperties {
    if (!pinned) return {};
    return {
        position: 'sticky',
        left: offset,
        zIndex: header ? 3 : 2,
        background: header ? 'var(--bm-color-table-header)' : 'var(--bm-color-surface)',
    };
}

export default function MegaGridHeader<T>(props: MegaGridHeaderProps<T>): ReactElement {
    const { columns, sorts, widths, pinned, pinnedOffsets, density, allowResize, onToggleSort, onResize } = props;
    const drag = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

    const beginResize = (key: string, startWidth: number) => (e: React.PointerEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        drag.current = { key, startX: e.clientX, startWidth };
        const move = (ev: PointerEvent): void => {
            if (!drag.current) return;
            onResize(drag.current.key, drag.current.startWidth + (ev.clientX - drag.current.startX));
        };
        const up = (): void => {
            drag.current = null;
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    return (
        <Table.Thead style={{ background: 'var(--bm-color-table-header)', color: 'var(--bm-color-table-header-text)' }}>
            <Table.Tr>
                {columns.map((col) => {
                    const sortIndex = sorts.findIndex((s) => s.key === col.key);
                    const sort = sortIndex >= 0 ? sorts[sortIndex] : undefined;
                    const width = widths[col.key] ?? (typeof col.width === 'number' ? col.width : undefined);
                    const isPinned = pinned.has(col.key);
                    const fontSize = density === 'compact' ? 10 : undefined;

                    return (
                        <Table.Th
                            key={col.key}
                            style={{
                                width,
                                minWidth: col.minWidth,
                                textAlign: col.align ?? 'left',
                                position: 'relative',
                                fontSize,
                                background: 'var(--bm-color-table-header)',
                                color: 'var(--bm-color-table-header-text)',
                                borderBottom: '1px solid var(--bm-color-border)',
                                ...pinnedStyle(isPinned, pinnedOffsets[col.key] ?? 0, true),
                            }}
                        >
                            <Group gap={4} wrap="nowrap" w="100%" justify={col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start'}>
                                {col.sortValue ? (
                                    <UnstyledButton
                                        onClick={(e) => onToggleSort(col.key, e.shiftKey)}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}
                                    >
                                        {col.header}
                                        {sort ? (
                                            sort.dir === 'asc' ? (
                                                <ChevronUp size={14} />
                                            ) : (
                                                <ChevronDown size={14} />
                                            )
                                        ) : (
                                            <ChevronsUpDown size={14} opacity={0.4} />
                                        )}
                                        {sorts.length > 1 && sortIndex >= 0 && (
                                            <span style={{ fontSize: 9, opacity: 0.6 }}>{sortIndex + 1}</span>
                                        )}
                                    </UnstyledButton>
                                ) : (
                                    <span>{col.header}</span>
                                )}
                            </Group>
                            {allowResize && col.resizable !== false && (
                                <div
                                    onPointerDown={beginResize(col.key, width ?? 160)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 6, cursor: 'col-resize', touchAction: 'none' }}
                                    aria-hidden
                                />
                            )}
                        </Table.Th>
                    );
                })}
            </Table.Tr>
        </Table.Thead>
    );
}
